import type { Link } from '@unhead/schema'
import { baseLinkConfig, baseMetaConfig } from '../config'

export const useMeta = (page?: string) => {
  const nuxtApp = useNuxtApp()
  const currentPharmacyStore = useCurrentPharmacyStore()
  const { getMetaUrl } = useCdnUrl()
  const { t, te, locale } = useTranslation()
  const route = useRoute()
  const url = useRequestURL()

  const robots = computed(() => {
    if (page && te(`meta.${page}.robots`))
      return t(`meta.${page}.robots`)

    else if (currentPharmacyStore.pharmacy)
      return currentPharmacyStore.pharmacy.additionalInformation.isTestPharmacy ? 'noindex, nofollow' : 'noindex, nofollow'

    return 'noindex, nofollow'
  })

  const metaUrl = computed(() => {
    return `${url.origin}${route.path}`
  })

  const pageTitle = computed(() => {
    if (page && te(`meta.${page}.pageTitle`))
      return t(`meta.${page}.pageTitle`)

    return t('meta.default.pageTitle')
  })

  const pageDescription = computed(() => {
    if (page && te(`meta.${page}.pageDescription`))
      return t(`meta.${page}.pageDescription`)

    return ''
  })

  const image = computed (() => {
    if (page && te(`meta.${page}.image`))
      return getMetaUrl(t(`meta.${page}.image`))

    return getMetaUrl(t('meta.default.image'))
  })

  const imageWidth = computed (() => {
    if (page && te(`meta.${page}.imageWidth`))
      return t(`meta.${page}.imageWidth`)

    return t('meta.default.imageWidth')
  })

  const imageHeight = computed (() => {
    if (page && te(`meta.${page}.imageHeight`))
      return t(`meta.${page}.imageHeight`)

    return t('meta.default.imageHeight')
  })

  const canonical = computed(() => {
    if (page && te(`meta.${page}.canonical`))
      return t(`meta.${page}.canonical`)

    return t('meta.default.canonical')
  })

  const ogPrice = computed(() => {
    if (page && te(`meta.${page}.ogPrice`))
      return t(`meta.${page}.ogPrice`)
  })

  const dynamicMetaConfig = {
    title: () => pageTitle.value,
    description: () => pageDescription.value,
    ogTitle: () => pageTitle.value,
    ogDescription: () => pageDescription.value,
    ogImage: () => image.value,
    ogImageWidth: () => imageWidth.value,
    ogImageHeight: () => imageHeight.value,
    ogUrl: () => metaUrl.value,
    twitterTitle: () => pageTitle.value,
    twitterDescription: () => pageDescription.value,
    twitterImage: () => image.value,
    twitterUrl: () => metaUrl.value,
    robots: () => robots.value,
    ogPrice: () => ogPrice.value,
  }

  const defaultMetaConfig = {
    ogLocale: locale,
    msapplicationTileImage: getMetaUrl('android-chrome-192x192.png'),
  }

  const linkConfig = <Link[]>[
    ...baseLinkConfig,
    { rel: 'apple-touch-icon', sizes: '57x57', href: getMetaUrl('android-chrome-57x57.png') },
    { rel: 'apple-touch-icon', sizes: '60x60', href: getMetaUrl('android-chrome-60x60.png') },
    { rel: 'apple-touch-icon', sizes: '72x72', href: getMetaUrl('android-chrome-72x72.png') },
    { rel: 'apple-touch-icon', sizes: '76x76', href: getMetaUrl('android-chrome-76x76.png') },
    { rel: 'apple-touch-icon', sizes: '114x114', href: getMetaUrl('android-chrome-114x114.png') },
    { rel: 'apple-touch-icon', sizes: '120x120', href: getMetaUrl('android-chrome-120x120.png') },
    { rel: 'apple-touch-icon', sizes: '144x144', href: getMetaUrl('android-chrome-144x144.png') },
    { rel: 'apple-touch-icon', sizes: '152x152', href: getMetaUrl('android-chrome-152x152.png') },
    { rel: 'apple-touch-icon', sizes: '180x180', href: getMetaUrl('android-chrome-180x180.png') },
    { rel: 'apple-touch-icon', href: getMetaUrl('android-chrome-192x192.png') },
    { rel: 'apple-touch-startup-image', href: getMetaUrl('android-chrome-192x192.png') },
    { rel: 'icon', type: 'image/png', sizes: '16x16', href: getMetaUrl('favicon-16x16.png') },
    { rel: 'icon', type: 'image/png', sizes: '32x32', href: getMetaUrl('favicon-32x32.png') },
    { rel: 'icon', type: 'image/png', sizes: '192x192', href: getMetaUrl('ia_favicon_192x192.png') },
  ]

  const dynamicLinkConfig = <Link[]>[
    { rel: 'canonical', href: canonical.value },
  ]

  return { defaultMetaConfig, dynamicMetaConfig, baseMetaConfig, linkConfig, dynamicLinkConfig, pageTitle }
}
